import { useEffect, Component } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { LanguageProvider } from "@/lang";
import Seo from "@/components/Seo";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Products from "@/pages/Products";
import Contact from "@/pages/Contact";
import Career from "@/pages/Career";
import NotFound from "@/pages/NotFound";

class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }
    static getDerivedStateFromError() {
        return { hasError: true };
    }
    render() {
        if (this.state.hasError) {
            return (
                <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-paper px-6 text-center">
                    <p className="font-mono text-xs uppercase tracking-[0.25em] text-sky-text">
                        System fault
                    </p>
                    <p className="text-2xl font-light tracking-tight text-ink">
                        Something went wrong.
                    </p>
                    <button
                        className="rounded-full border border-line px-6 py-3 font-mono text-xs uppercase tracking-[0.2em] text-ink hover:border-ink"
                        onClick={() => window.location.reload()}
                        data-testid="error-reload-button"
                    >
                        Reload
                    </button>
                </div>
            );
        }
        return this.props.children;
    }
}

function ScrollToTop() {
    const { pathname } = useLocation();
    useEffect(() => {
        if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
        window.scrollTo(0, 0);
    }, [pathname]);
    return null;
}

function SmoothScroll() {
    useEffect(() => {
        const lenis = new Lenis({
            duration: 1.15,
            easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
            smoothWheel: true,
        });
        window.__lenis = lenis;
        let running = true;
        const loop = (time) => {
            if (!running) return;
            lenis.raf(time);
            requestAnimationFrame(loop);
        };
        requestAnimationFrame(loop);
        return () => {
            running = false;
            lenis.destroy();
            window.__lenis = null;
        };
    }, []);
    return null;
}

function App() {
    return (
        <ErrorBoundary>
            <LanguageProvider>
                <BrowserRouter>
                    <SmoothScroll />
                    <ScrollToTop />
                    <Seo />
                    <Navbar />
                    <main>
                        <Routes>
                            <Route path="/" element={<Home />} />
                            <Route path="/about" element={<About />} />
                            <Route path="/products" element={<Products />} />
                            <Route path="/career" element={<Career />} />
                            <Route path="/contact" element={<Contact />} />
                            <Route path="*" element={<NotFound />} />
                        </Routes>
                    </main>
                    <Footer />
                </BrowserRouter>
            </LanguageProvider>
        </ErrorBoundary>
    );
}

export default App;
